import type { CSSProperties, ReactNode } from 'react';
import type { DayBar, Kpi, TagClass } from './data/types';

/** Small building blocks that reproduce repeated V1 markup exactly (inline styles copied from the reference template). */

export const Icon = ({ name, style }: { name: string; style?: CSSProperties }) => <i className={`ph ${name}`} style={style} />;

export const Tag = ({ cls, children, style }: { cls: TagClass | string; children: ReactNode; style?: CSSProperties }) => (
  <span className={`tag ${cls}`} style={{ whiteSpace: 'nowrap', flex: 'none', ...style }}>{children}</span>
);

export const PageHeader = ({ kicker, title, children, wrap = true }: { kicker: ReactNode; title: string; children?: ReactNode; wrap?: boolean }) => (
  <header style={{ display: 'flex', alignItems: 'flex-end', gap: 'var(--space-6)', flexWrap: wrap ? 'wrap' : undefined }}>
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <h6 style={{ color: 'var(--color-accent)', margin: 0 }}>{kicker}</h6>
      <h2 style={{ margin: 0 }}>{title}</h2>
    </div>
    {children}
  </header>
);

export function Seg<T extends string>({ name, options, value, onChange, style }: { name: string; options: { value: T; label: string }[]; value: T; onChange?: (v: T) => void; style?: CSSProperties }) {
  return (
    <div className="seg" style={style}>
      {options.map((o) => (
        <label key={o.value} className="seg-opt">
          <input type="radio" name={name} checked={value === o.value} onChange={() => onChange?.(o.value)} />
          <span>{o.label}</span>
        </label>
      ))}
    </div>
  );
}

export const KpiCard = ({ k }: { k: Kpi }) => (
  <div className="card elev-sm" style={{ gap: 2 }}>
    <div className="card-kicker">{k.label}</div>
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
      <span style={{ fontFamily: 'var(--font-heading)', fontSize: 26, lineHeight: 1.1 }}>{k.value}</span>
      <span style={{ fontSize: 12, color: 'var(--color-accent-300)' }}>{k.delta}</span>
    </div>
    <div className="card-meta">{k.note}</div>
  </div>
);

export const KpiGrid = ({ kpis }: { kpis: Kpi[] }) => (
  <section style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 'var(--space-3)' }}>
    {kpis.map((k) => <KpiCard key={k.label} k={k} />)}
  </section>
);

export const ReachBars = ({ days, height }: { days: DayBar[]; height: number }) => (
  <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height }}>
    {days.map((d) => (
      <div key={d.label} title={d.label} style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', gap: 2, height: '100%' }}>
        <div style={{ height: `${d.paid}%`, background: 'var(--color-accent-800)', borderRadius: '2px 2px 0 0' }} />
        <div style={{ height: `${d.org}%`, background: 'var(--color-accent-500)', borderRadius: 2 }} />
      </div>
    ))}
  </div>
);

export const Meter = ({ pct, height = 6, color = 'var(--color-accent-600)', width }: { pct: number; height?: number; color?: string; width?: number }) => (
  <div style={{ width, height, borderRadius: height / 2, background: 'var(--color-neutral-900)', overflow: 'hidden', ...(width ? {} : { flex: undefined }) }}>
    <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: height / 2 }} />
  </div>
);

export const MiniStat = ({ label, children }: { label: string; children: ReactNode }) => (
  <div>
    <div className="text-muted" style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</div>
    <div style={{ fontVariantNumeric: 'tabular-nums' }}>{children}</div>
  </div>
);

export const sevTag = (v: string): TagClass => (v === 'high' ? 'tag-accent' : v === 'medium' ? 'tag-outline' : 'tag-neutral');
export const tabular: CSSProperties = { fontVariantNumeric: 'tabular-nums' };
